import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import SubscriptionBanner from "./SubscriptionBanner";
import ImpersonationBanner from "./ImpersonationBanner";
import DemoBanner from "./DemoBanner";
import CommandThemeScope from "./CommandThemeScope";
import { HelpChatWidget } from "./help/HelpChatWidget";
import { HelpChatProvider } from "../contexts/HelpChatContext";
import { PageHeaderProvider, usePageHeaderValue } from "../contexts/PageHeaderContext";

// The single Topbar instance for the whole app — reads whatever the
// currently-rendered page registered via usePageHeader(). Pages that
// haven't migrated yet still render their own <Topbar/>, so this stays
// silent (renders nothing) until a page actually registers a header.
function LayoutTopbar() {
  const { title, subtitle, actions } = usePageHeaderValue();
  if (!title && !subtitle && !actions) return null;
  return <Topbar title={title} subtitle={subtitle} actions={actions} />;
}

export default function Layout() {
  const location = useLocation();
  // The mobile Home shell (/mobile, /dashboard) keeps its own deeper blue
  // gradient hero. Every other mobile page now sits on the app-wide light
  // #1484F6→#FAFBFC gradient (set once here, not per-page) — pages/cards
  // are expected to bring their own white/semi-transparent surfaces on top
  // for legibility, same pattern as the Create Job redesign.
  const isMobileHomeShell = location.pathname === "/mobile" || location.pathname === "/dashboard";

  return (
    <div
      className={`min-h-screen h-screen overflow-hidden flex bg-[#0A0F1C] text-slate-200 ${
        isMobileHomeShell
          ? "max-[767px]:bg-gradient-to-b max-[767px]:from-[#0077CC] max-[767px]:to-[#66B2E6] max-[767px]:bg-fixed"
          : "max-[767px]:bg-gradient-to-b max-[767px]:from-[#1484F6] max-[767px]:to-[#FAFBFC] max-[767px]:bg-fixed"
      }`}
      data-testid="app-layout"
    >
      <CommandThemeScope surface="desktop" />
      <HelpChatProvider>
        <Sidebar />
        <main className="flex-1 min-w-0 min-h-0 flex flex-col overflow-y-auto pt-14 md:pt-0 pb-20 md:pb-0">
          <PageHeaderProvider>
            <DemoBanner />
            <ImpersonationBanner />
            <SubscriptionBanner />
            <LayoutTopbar />
            {/* Root-level fix: without this, every mobile page (a `min-h-screen`
                block) is a flex item of this flex-col `<main>` with the browser
                default `flex-shrink: 1`. Since `<main>` has a bounded height
                (h-screen ancestor + flex-1 min-h-0), flex-shrink forces each
                mobile page to be squashed down to exactly viewport height —
                clipped at its own `min-height: 100vh` floor — regardless of how
                tall its real content is. That silently swallows the last chunk
                of every mobile page's content (and its own bottom-nav-clearance
                padding, which ends up positioned above the true content end).
                `max-md:shrink-0` fixes it for every current/future mobile page
                without touching desktop, where some pages (e.g. DispatchBoard)
                intentionally rely on shrink-to-fit + internal panel scrolling. */}
            <div className="max-md:shrink-0">
              <Outlet />
            </div>
          </PageHeaderProvider>
        </main>
        <HelpChatWidget />
      </HelpChatProvider>
    </div>
  );
}
